"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeft, Bell, ExternalLink, Star, Scale, ListPlus,
  Coins, Tag, Sparkles, ChevronDown, Megaphone, Truck, Package, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ProductImageGallery } from "@/components/sections/comparador/product-image-gallery";
import dynamic from "next/dynamic";
import { ShareMenu } from "@/components/ui/share-menu";
import { NutritionSeals } from "@/components/sections/comparador/nutrition-seals";
import { ReactionRenderer } from "@/components/reactions/rich-text";
import { useFlag } from "@/lib/flags-context";
import { scrollToElementWithOffset } from "@/lib/sticky-offset";
import { formatCurrency, calculateDiscount, cn } from "@/lib/utils";
import { preferOptimizableImages } from "@/lib/image-host";
import { GoLink } from "@/components/go-link";
import type { Product, ProductPrice, ProductReview } from "@/types";
import { BETWEEN_STORES_EVERY, betweenStoresSection } from "@/lib/ad-placements";

const AdSlotClient = dynamic(
  () => import("@/components/ads/ad-slot-client").then((m) => m.AdSlotClient),
  { ssr: false }
);
const TipTapRenderer = dynamic(
  () => import("@/components/ui/tiptap-renderer").then((m) => m.TipTapRenderer),
  { ssr: false, loading: () => null }
);
// La gráfica usa recharts (~78 KiB) y vive debajo del pliegue, así que la
// cargamos bajo demanda para sacarla del bundle crítico de la página.
const MultiStorePriceChart = dynamic(
  () =>
    import("@/components/sections/comparador/multi-store-price-chart").then(
      (m) => m.MultiStorePriceChart
    ),
  { ssr: false, loading: () => <div className="h-64 animate-pulse rounded-lg bg-muted" /> }
);
const ReviewForm = dynamic(
  () => import("@/components/sections/comparador/review-form").then((m) => m.ReviewForm),
  { ssr: false }
);
const PriceAlertModal = dynamic(
  () => import("@/components/sections/comparador/price-alert-modal").then((m) => m.PriceAlertModal),
  { ssr: false }
);
const LoginPromptModal = dynamic(
  () => import("@/components/sections/comparador/login-prompt-modal").then((m) => m.LoginPromptModal),
  { ssr: false }
);
const AddToListModal = dynamic(
  () => import("@/components/sections/comparador/add-to-list-modal").then((m) => m.AddToListModal),
  { ssr: false }
);
const StoreInfoDialog = dynamic(
  () => import("@/components/sections/comparador/store-info-dialog").then((m) => m.StoreInfoDialog),
  { ssr: false }
);
const NutritionTable = dynamic(
  () => import("@/components/sections/comparador/nutrition-table").then((m) => m.NutritionTable),
  { ssr: false }
);

interface ProductDetailProps {
  product: Product;
  referer?: string | null;
  isAuthenticated?: boolean;
  /** Mapa storeDbId|url → clickId pre-generado server-side */
  clickIdMap?: Record<string, number>;
}

type SortKey = "price_asc" | "price_desc" | "name_asc" | "name_desc";
type SectionId = "storesprices_v1_1" | "opiniones" | "historial" | "detalles" | "caracteristicas";

const SORT_LABELS: Record<SortKey, string> = {
  price_asc: "Precio más bajo",
  price_desc: "Precio más alto",
  name_asc: "Nombre A-Z",
  name_desc: "Nombre Z-A",
};

function StoreLogoCircle({ name, src, size = 36 }: { name: string; src?: string; size?: number }) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="rounded-full bg-muted overflow-hidden inline-flex items-center justify-center shrink-0 border"
      style={{ width: size, height: size }}
    >
      {src ? (
        <Image src={src} alt="" width={size} height={size} className="object-cover w-full h-full" loading="lazy" />
      ) : (
        <span className="text-[10px] font-bold text-muted-foreground">{initials}</span>
      )}
    </span>
  );
}

function StarsRow({ value, size = 14 }: { value: number; size?: number }) {
  return (
    <div
      role="img"
      className="flex items-center gap-0.5"
      aria-label={`${value} de 5 estrellas`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          aria-hidden="true"
          className={cn(value >= n ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30")}
          style={{ width: size, height: size }}
        />
      ))}
    </div>
  );
}

function inferRefererLabel(referer: string | null | undefined): { label: string; href: string } {
  if (!referer) return { label: "Inicio", href: "/" };
  try {
    const u = new URL(referer);
    const seg = u.pathname.split("/").filter(Boolean)[0];
    const map: Record<string, string> = {
      comparador: "Comparador",
      ofertas: "Ofertas",
      cupones: "Cupones",
      tiendas: "Tiendas",
      marcas: "Marcas",
      buscar: "Búsqueda",
      blog: "Blog",
    };
    if (!seg) return { label: "Inicio", href: "/" };
    return { label: map[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1), href: `/${seg}` };
  } catch {
    return { label: "Inicio", href: "/" };
  }
}

/**
 * Resuelve la línea de envío de un precio.
 *
 * `cost` es la etiqueta corta ("+$49 envío" / "Envío gratis") que se pinta en
 * móvil, y `eta` el tiempo de entrega que solo se añade de tableta en
 * adelante, donde hay espacio. Prioriza lo que trajo el scrape y cae a las
 * condiciones editables por tienda (`Store.shippingTerms`, y como último
 * recurso `Store.shippingSummary`), para que la columna junto al precio no
 * quede vacía.
 */
function resolveShipping(
  p: ProductPrice
): { cost: string; eta: string | null; free: boolean } | null {
  const eta = p.shippingDays ?? p.storeShippingEta ?? null;
  const amount = p.shippingCost ?? p.storeShippingCost ?? null;

  if (p.shippingFree || amount === 0) {
    return { cost: "Envío gratis", eta, free: true };
  }
  if (amount != null) {
    return { cost: `+${formatCurrency(amount)} envío`, eta, free: false };
  }
  const raw = p.shipping ?? p.storeShippingSummary;
  if (raw) return { cost: raw, eta, free: /gratis/i.test(raw) };
  return eta ? { cost: "Envío", eta, free: false } : null;
}

function ShippingLabel({ p }: { p: ProductPrice }) {
  const s = resolveShipping(p);
  if (!s) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        s.free ? "text-green-700" : "text-muted-foreground"
      )}
    >
      <Truck className="w-3 h-3 shrink-0" /> {s.cost}
      {s.eta ? ` · ${s.eta}` : ""}
    </span>
  );
}

function BenefitChip({ p }: { p: ProductPrice }) {
  const communityOn = useFlag("community");
  const couponsOn = useFlag("coupons");
  const cashbackOn = useFlag("cashback");
  if (communityOn && p.communityDealId) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
        <Sparkles className="w-3 h-3" /> Oferta comunidad
      </span>
    );
  }
  if (couponsOn && p.couponCode) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
        <Tag className="w-3 h-3" /> Cupón {p.couponCode}
      </span>
    );
  }
  if (cashbackOn && p.cashbackRate) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
        <Coins className="w-3 h-3" /> {p.cashbackRate}% cashback
      </span>
    );
  }
  return null;
}

function PriceAlertButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
    >
      <Bell className="w-4 h-4" /> Alerta de precio
    </button>
  );
}

const TABS: Array<{ id: SectionId; label: string | ((p: Product) => string) }> = [
  { id: "storesprices_v1_1", label: "Precios" },
  { id: "opiniones", label: "Opiniones" },
  { id: "historial", label: "Historial" },
  { id: "detalles", label: "Detalles" },
  { id: "caracteristicas", label: (p) => (p.specsKind === "NUTRITION" ? "Nutrimental" : "Características") },
];

function LazySection({
  id,
  children,
  refMap,
  eager = false,
}: {
  id: SectionId;
  children: React.ReactNode;
  refMap: React.MutableRefObject<Partial<Record<SectionId, HTMLElement | null>>>;
  /** Primera sección above-the-fold: sin skeleton→contenido (CLS). */
  eager?: boolean;
}) {
  const [mounted, setMounted] = useState(eager);
  const localRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (mounted) return;
    const el = localRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setMounted(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: "300px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [mounted]);

  return (
    <section
      id={`section-${id}`}
      ref={(el) => {
        localRef.current = el;
        refMap.current[id] = el;
      }}
      className="scroll-mt-24"
    >
      {mounted ? children : (
        <div className="h-40 rounded-2xl border bg-muted/20 animate-pulse" aria-hidden />
      )}
    </section>
  );
}

export function ProductDetail({ product, referer, isAuthenticated = false, clickIdMap = {} }: ProductDetailProps) {
  const [alertOpen, setAlertOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [loginPromptKind, setLoginPromptKind] = useState<"community" | "price-alert">("community");
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(isAuthenticated);
  const shoppingListEnabled = useFlag("products.shoppingList");
  const brandLinkEnabled = useFlag("products.brandLink");
  const categoryLinkEnabled = useFlag("products.categoryLink");
  const [sort, setSort] = useState<SortKey>("price_asc");
  const [sortOpen, setSortOpen] = useState(false);
  const [active, setActive] = useState<SectionId>("storesprices_v1_1");
  const sectionsRef = useRef<Partial<Record<SectionId, HTMLElement | null>>>({});
  const storeRowRefs = useRef<Record<string, HTMLElement | null>>({});

  function handleReviewClick() {
    if (userIsAuthenticated) {
      setShowReview(true);
    } else {
      setLoginPromptKind("community");
      setLoginPromptOpen(true);
    }
  }

  function handleAddToListClick() {
    if (!shoppingListEnabled) return;
    if (userIsAuthenticated) {
      setAddToListOpen(true);
    } else {
      setLoginPromptKind("community");
      setLoginPromptOpen(true);
    }
  }

  function handlePriceAlertClick() {
    void fetch("/api/search/track", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        query: product.name,
        type: "activation:price_alert_cta",
        fromProductId: product.id,
        fromProductName: product.name,
      }),
      keepalive: true,
    }).catch(() => undefined);
    if (userIsAuthenticated) {
      setAlertOpen(true);
      return;
    }
    setLoginPromptKind("price-alert");
    setLoginPromptOpen(true);
  }

  const sortedPrices = useMemo(() => {
    const arr = [...product.prices];
    // Las filas agotadas conservan su último precio para el historial, pero
    // nunca deben ganar como la oferta más barata ni desplazar a una vigente.
    // Primero se muestran las vigentes; dentro de cada grupo se respeta el
    // criterio elegido por la persona.
    arr.sort((a, b) => {
      if (a.inStock !== b.inStock) return a.inStock ? -1 : 1;
      switch (sort) {
        case "price_asc": return a.price - b.price;
        case "price_desc": return b.price - a.price;
        case "name_asc": return a.storeName.localeCompare(b.storeName);
        case "name_desc": return b.storeName.localeCompare(a.storeName);
      }
      return 0;
    });
    return arr;
  }, [product.prices, sort]);

  const pricesByLow = useMemo(() => {
    const available = product.prices.filter((price) => price.inStock);
    const source = available.length > 0 ? available : product.prices;
    return [...source].sort((a, b) => a.price - b.price);
  }, [product.prices]);
  const cheapest = pricesByLow[0];
  const top5 = pricesByLow.slice(0, 5);
  const promotedPrice = product.prices.find((p) => p.isPromoted);
  const cheapestOriginal = cheapest?.originalPrice && cheapest.originalPrice > cheapest.price
    ? cheapest.originalPrice
    : null;
  const discount = cheapestOriginal ? calculateDiscount(cheapestOriginal, cheapest!.price) : 0;
  const ratingAverage = product.ratingAverage ?? 0;
  const ratingCount = product.ratingCount ?? 0;
  const distribution = product.ratingDistribution ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const refererCrumb = inferRefererLabel(referer);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const run = () => {
      fetch("/api/auth/session", { cache: "no-store", signal: controller.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((session) => {
          if (!cancelled) setUserIsAuthenticated(Boolean(session?.user));
        })
        .catch(() => undefined);
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 2500 })
      : window.setTimeout(run, 1200);
    return () => {
      cancelled = true;
      controller.abort();
      if (window.cancelIdleCallback && typeof idleId === "number") {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId as number);
      }
    };
  }, []);

  // Si la persona inició sesión desde el CTA de alerta, retomamos exactamente
  // la acción que intentaba completar en lugar de obligarla a buscarla otra vez.
  useEffect(() => {
    if (!userIsAuthenticated || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("priceAlert") !== "1") return;
    setAlertOpen(true);
    url.searchParams.delete("priceAlert");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [userIsAuthenticated]);

  useEffect(() => {
    const attribution = new URLSearchParams(window.location.search);
    const sourceParam = attribution.get("utm_source");
    const mediumParam = attribution.get("utm_medium");
    const cleanSource = sourceParam && /^[a-z0-9_-]{1,32}$/i.test(sourceParam)
      ? sourceParam.toLowerCase()
      : null;
    const source = cleanSource
      ? mediumParam === "share"
        ? `share:${cleanSource}`
        : mediumParam === "retention"
          ? `retention:${cleanSource}`
          : `campaign:${cleanSource}`
      : document.referrer ? "product-page" : "direct";
    const payload = JSON.stringify({
      source,
    });
    const url = `/api/products/${encodeURIComponent(product.id)}/view`;
    const send = () => {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        return;
      }
      fetch(url, {
        method: "POST",
        body: payload,
        headers: { "content-type": "application/json" },
        keepalive: true,
      }).catch(() => undefined);
    };
    const timeout = window.setTimeout(send, 1800);
    return () => window.clearTimeout(timeout);
  }, [product.id]);

  const allImages = preferOptimizableImages(
    product.images?.length
      ? product.images
      : product.imageUrl
        ? [product.imageUrl]
        : [],
  );

  const sharePayload = useMemo(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const currentPrices = product.prices
      .filter((price) => price.inStock)
      .map((price) => price.price)
      .filter((price) => Number.isFinite(price) && price > 0);
    const lowestValue = currentPrices.length > 0 ? Math.min(...currentPrices) : product.lowestPrice;
    const highestValue = currentPrices.length > 1 ? Math.max(...currentPrices) : null;
    const lowest = lowestValue ? formatCurrency(lowestValue) : null;
    const highest = highestValue && highestValue > (lowestValue ?? 0)
      ? formatCurrency(highestValue)
      : null;
    const storeCount = new Set(product.prices.map((price) => price.storeId)).size;
    const comparison = lowest && highest && storeCount > 1
      ? `${product.name}: lo encontré de ${lowest} a ${highest} en ${storeCount} tiendas. Compara antes de comprar en SuCom`
      : lowest
        ? `${product.name} — desde ${lowest} en SuCom`
        : product.name;
    return {
      title: product.name,
      text: comparison,
      url,
      imageUrl: product.imageUrl,
    };
  }, [product.name, product.lowestPrice, product.imageUrl, product.prices]);

  function scrollToSection(id: SectionId) {
    const el = sectionsRef.current[id];
    if (!el) return;
    setActive(id);
    // Use the shared sticky-offset helper so navbar + any sticky ad
    // banner are accounted for, instead of letting the section hide
    // behind the chrome.
    scrollToElementWithOffset(el, 12);
  }

  // Highlight active tab as the user scrolls.
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const ordered: SectionId[] = ["storesprices_v1_1", "opiniones", "historial", "detalles", "caracteristicas"];
    const visibility: Record<SectionId, number> = {
      storesprices_v1_1: 0, opiniones: 0, historial: 0, detalles: 0, caracteristicas: 0,
    };
    ordered.forEach((id) => {
      const el = sectionsRef.current[id];
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            visibility[id] = e.intersectionRatio;
          }
          let bestId: SectionId = "storesprices_v1_1";
          let bestRatio = -1;
          for (const sid of ordered) {
            if (visibility[sid] > bestRatio) {
              bestRatio = visibility[sid];
              bestId = sid;
            }
          }
          if (bestRatio > 0) setActive(bestId);
        },
        { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Banner cada 5 tiendas
  // Anuncio PRODUCT_BETWEEN_STORES cada 5 tiendas; `position` (1, 2, 3…) es
  // la sección que cada creativo puede elegir en el admin.
  const sortedWithAds: Array<{ kind: "store"; price: ProductPrice } | { kind: "ad"; position: number }> = [];
  sortedPrices.forEach((p, i) => {
    sortedWithAds.push({ kind: "store", price: p });
    if ((i + 1) % BETWEEN_STORES_EVERY === 0 && i < sortedPrices.length - 1) {
      sortedWithAds.push({ kind: "ad", position: (i + 1) / BETWEEN_STORES_EVERY });
    }
  });

  return (
    <div className="min-h-screen bg-muted/30 pb-24 lg:pb-0">
      <div className="container-app py-6">
        {/* Breadcrumb */}
        <nav
          aria-label="Ruta de navegación"
          className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap min-h-10 content-start"
        >
          <Link href={refererCrumb.href} className="hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {refererCrumb.label}
          </Link>
          <span aria-hidden="true">/</span>
          <Link href="/comparador" className="hover:text-foreground">Productos</Link>
          {product.categoryPath && product.categoryPath.length > 0 ? (
            // Ruta jerárquica completa: categoría raíz → … → subcategoría hoja.
            product.categoryPath.map((node, i, arr) => {
              const isLast = i === arr.length - 1;
              const href = node.subSlug
                ? `/categoria/${node.categorySlug}/${node.subSlug}`
                : `/categoria/${node.categorySlug}`;
              return (
                <span key={`${node.categorySlug}-${node.subSlug ?? "root"}`} className="flex items-center gap-2">
                  <span aria-hidden="true">/</span>
                  {isLast ? (
                    <span className="text-foreground font-medium">{node.name}</span>
                  ) : (
                    <Link href={href} className="hover:text-foreground">{node.name}</Link>
                  )}
                </span>
              );
            })
          ) : (
            <>
              {product.category && (
                <>
                  <span aria-hidden="true">/</span>
                  <Link
                    href={product.categorySlug ? `/comparador?category=${product.categorySlug}` : "/comparador"}
                    className="hover:text-foreground"
                  >
                    {product.category}
                  </Link>
                </>
              )}
              {product.subcategory && (
                <>
                  <span aria-hidden="true">/</span>
                  <span className="text-foreground font-medium">{product.subcategory}</span>
                </>
              )}
            </>
          )}
        </nav>

        {/* HEADER full width: gallery + info + top5 (sin sidebar arriba) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Galería */}
          <div className="lg:col-span-5">
            <ProductImageGallery
              images={allImages}
              alt={product.name}
              onOpenAlert={handlePriceAlertClick}
              onAddToList={shoppingListEnabled ? handleAddToListClick : undefined}
            />
          </div>

          {/* Info principal */}
          <div className="lg:col-span-4 space-y-3 min-w-0">
            <div className="flex items-center gap-2">
              {categoryLinkEnabled && product.categorySlug ? (
                <Link href={`/categoria/${product.categorySlug}`} aria-label={`Ver categoría ${product.category}`}>
                  <Badge variant="blue" className="text-xs hover:bg-blue-200/80 transition-colors cursor-pointer">
                    {product.category}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="blue" className="text-xs">{product.category}</Badge>
              )}
              {product.presentation && (
                <Badge variant="outline" className="text-xs">{product.presentation}</Badge>
              )}
              {product.isBasicBasket && (
                <Link href="/comparador/canastas" aria-label="Ver canastas básicas">
                  <Badge variant="outline" className="text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-50 cursor-pointer">
                    Canasta básica
                  </Badge>
                </Link>
              )}
            </div>
            {brandLinkEnabled && product.brandSlug ? (
              <Link
                href={`/marca/${product.brandSlug}`}
                className="inline-block text-sm text-muted-foreground font-medium hover:text-primary hover:underline"
              >
                {product.brand}
              </Link>
            ) : product.brand ? (
              <p className="text-sm text-muted-foreground font-medium">{product.brand}</p>
            ) : null}
            <h1 className="text-2xl font-bold leading-snug">{product.name}</h1>

            <button
              type="button"
              onClick={handlePriceAlertClick}
              className="flex w-full items-center gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Bell className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-foreground">Vigila este precio gratis</span>
                <span className="block text-xs leading-5 text-muted-foreground">
                  {cheapest ? `Te avisamos si baja de ${formatCurrency(cheapest.price)}.` : "Te avisamos cuando encuentre un precio más bajo."}
                </span>
              </span>
              <span className="shrink-0 text-xs font-semibold text-primary">Activar →</span>
            </button>

            <div className="flex justify-end lg:hidden">
              <ShareMenu
                payload={sharePayload}
                label="Compartir comparación"
                buttonClassName="border-primary/20 text-primary"
                oneTap
                analytics={{
                  query: product.name,
                  type: "activation:share_intent",
                  fromProductId: product.id,
                  fromProductName: product.name,
                }}
              />
            </div>

            <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
              <button
                type="button"
                onClick={() => scrollToSection("opiniones")}
                className="flex items-center gap-1.5 hover:text-foreground"
                aria-label={`Ver opiniones: ${ratingAverage ? ratingAverage.toFixed(1) : "sin calificación"} de 5, ${ratingCount} reseñas`}
              >
                <Star aria-hidden="true" className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium text-foreground">
                  {ratingAverage ? ratingAverage.toFixed(1) : "—"}
                </span>
                <span>({ratingCount})</span>
              </button>
              <span className="text-muted-foreground/50">|</span>
              <PriceAlertButton onClick={handlePriceAlertClick} />
              <span className="text-muted-foreground/50">|</span>
              <Link href={`/comparador?compare=${product.id}`} className="flex items-center gap-1.5 hover:text-foreground font-medium text-primary">
                <Scale className="w-4 h-4" /> Comparar
              </Link>
            </div>

            {product.descriptionContent ? (
              <TipTapRenderer doc={product.descriptionContent} className="prose-sm text-muted-foreground" />
            ) : product.description ? (
              <p className="text-sm text-muted-foreground leading-relaxed">
                <ReactionRenderer
                  text={product.description}
                  filter={{ scope: "DEAL", module: "products", contentType: "product" }}
                />
              </p>
            ) : null}

            {/* Precio principal (sin tarjeta). min-h en la fila de precio evita
                micro-CLS cuando aparecen tachado/badge tras hidratar. */}
            <div className="space-y-3 pt-1">
              <div>
                <p className="text-xs text-muted-foreground">Desde</p>
                <div className="flex items-baseline gap-2 flex-wrap min-h-10">
                  {cheapestOriginal && (
                    <span className="text-base text-muted-foreground line-through">
                      {formatCurrency(cheapestOriginal)}
                    </span>
                  )}
                  <span className="text-3xl font-black text-green-600">
                    {cheapest ? formatCurrency(cheapest.price) : "—"}
                  </span>
                  {discount > 0 && (
                    <Badge variant="hot" className="text-xs">-{discount}%</Badge>
                  )}
                </div>
              </div>

              {cheapest && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Mejor precio:</span>
                    <StoreLogoCircle name={cheapest.storeName} src={cheapest.storeLogo} size={28} />
                    <span className="font-semibold">{cheapest.storeName}</span>
                  </div>
                  <Button asChild className="w-full">
                    <GoLink storeId={cheapest.storeId} storeDbId={cheapest.storeDbId} url={cheapest.url} productId={product.id} tab="producto" clickId={clickIdMap[`${cheapest.storeDbId}|${cheapest.url}`]}>
                      Ir a la tienda <ExternalLink className="w-4 h-4 ml-1" />
                    </GoLink>
                  </Button>
                  <Button type="button" variant="outline" className="w-full" onClick={handlePriceAlertClick}>
                    <Bell className="w-4 h-4 mr-1" /> Avísame cuando baje
                  </Button>
                </>
              )}

              <NutritionSeals
                nutrition={(product as { nutrition?: any }).nutrition}
                madeInMexico={product.madeInMexico === true}
              />
            </div>
          </div>

          {/* Tabla top 5 */}
          <div className="lg:col-span-3 min-w-0">
            <div className="bg-background rounded-2xl border overflow-hidden">
              <div className="p-3 border-b flex items-center justify-between bg-muted/40">
                <span className="text-xs font-semibold uppercase tracking-wide">Mejores precios</span>
                <button
                  type="button"
                  onClick={handlePriceAlertClick}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Bell className="w-3 h-3" /> Configurar alerta
                </button>
              </div>
              <ul className="divide-y">
                {top5.map((p, i) => (
                  <li key={`${p.storeId}-${i}`} className="p-3 flex items-center gap-2">
                    <StoreLogoCircle name={p.storeName} src={p.storeLogo} size={28} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{p.storeName}</p>
                      <div className="flex items-baseline gap-1">
                        {p.originalPrice && p.originalPrice > p.price && (
                          <span className="text-[10px] text-muted-foreground line-through">
                            {formatCurrency(p.originalPrice)}
                          </span>
                        )}
                        <span className="text-sm font-bold text-green-700">{formatCurrency(p.price)}</span>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="outline" className="shrink-0 h-7 px-2 text-xs">
                      <GoLink storeId={p.storeId} storeDbId={p.storeDbId} url={p.url} productId={product.id} tab="producto" clickId={clickIdMap[`${p.storeDbId}|${p.url}`]}>Ir</GoLink>
                    </Button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => scrollToSection("storesprices_v1_1")}
                className="w-full text-xs font-medium text-primary py-2.5 border-t hover:bg-muted/50"
              >
                Ver todas las tiendas
              </button>
            </div>

            {/* Compartir, lista, SKU */}
            <div className="mt-3 text-xs space-y-2">
              <ShareMenu
                payload={sharePayload}
                label="Compartir"
                buttonClassName="border-0 hover:bg-muted/60 text-muted-foreground hover:text-foreground h-auto px-0 py-1 justify-start"
                analytics={{
                  query: product.name,
                  type: "activation:share_intent",
                  fromProductId: product.id,
                  fromProductName: product.name,
                }}
              />
              {shoppingListEnabled && (
                <button
                  type="button"
                  onClick={handleAddToListClick}
                  className="flex items-center gap-2 hover:text-foreground text-muted-foreground w-full"
                >
                  <ListPlus className="w-4 h-4" /> Añadir a lista
                </button>
              )}
              {product.ean && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="w-4 h-4" />
                  <span>EAN: <span className="font-mono">{product.ean}</span></span>
                </div>
              )}
              {product.sku && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="w-4 h-4" />
                  <span>SKU: <span className="font-mono">{product.sku}</span></span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* "ltr": tabs scrolleables + sidebar 300x600 únicamente aquí */}
        <div className="ltr grid grid-cols-1 min-[1200px]:grid-cols-[minmax(0,1fr)_300px] gap-6">
          <div className="min-w-0">
            {/* Sticky: section sub-nav + horizontal store carousel */}
            <div
              data-sticky-offset="true"
              className="sticky top-16 z-20 -mx-2 px-2 mb-4 bg-muted/30 backdrop-blur-sm supports-backdrop-filter:bg-muted/40 pb-2"
            >
              {/* Section sub-nav (horizontally scrollable on small screens) */}
              <div className="overflow-x-auto scrollbar-hide -mx-2 px-2">
                <div className="inline-flex gap-1 bg-background border rounded-xl p-1 shadow-xs">
                  {TABS.map((t) => {
                    const label = typeof t.label === "function" ? t.label(product) : t.label;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => scrollToSection(t.id)}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                          active === t.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            <div className="space-y-10">
              {/* STORES PRICES (storesprices_v1_1) */}
              <LazySection id="storesprices_v1_1" refMap={sectionsRef} eager>
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-bold">Precios en tiendas</h2>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setSortOpen((v) => !v)}
                          className="h-9 px-3 rounded-lg border bg-background text-sm flex items-center gap-2 hover:bg-muted"
                          aria-haspopup="menu"
                          aria-expanded={sortOpen}
                        >
                          Ordenar: <span className="font-semibold">{SORT_LABELS[sort]}</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        {sortOpen && (
                          <div role="menu" className="absolute z-10 mt-1 right-0 w-56 bg-background rounded-xl border shadow-lg p-1">
                            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                              <button
                                key={k}
                                type="button"
                                role="menuitemradio"
                                aria-checked={sort === k}
                                onClick={() => { setSort(k); setSortOpen(false); }}
                                className={cn(
                                  "w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted",
                                  sort === k && "bg-muted font-medium"
                                )}
                              >
                                {SORT_LABELS[k]}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={handlePriceAlertClick}>
                        <Bell className="w-4 h-4 mr-1" /> Alerta
                      </Button>
                    </div>
                  </div>

                  {promotedPrice && <PromotedStoreCard price={promotedPrice} productId={product.id} clickId={clickIdMap[`${promotedPrice.storeDbId}|${promotedPrice.url}`]} />}

                  <div className="space-y-3">
                    {sortedWithAds.map((row, idx) => {
                      if (row.kind === "ad") {
                        return (
                          <AdSlotClient
                            key={`ad-${row.position}`}
                            placement="PRODUCT_BETWEEN_STORES"
                            section={betweenStoresSection(row.position)}
                            category={product.category}
                            brand={product.brand}
                            productId={product.id}
                            className="w-full"
                          />
                        );
                      }
                      return (
                        <div
                          key={`${row.price.storeId}-${idx}`}
                          ref={(el) => {
                            storeRowRefs.current[row.price.storeId] = el;
                          }}
                          className="rounded-2xl transition-shadow"
                        >
                          <StoresProduct price={row.price} productId={product.id} productName={product.name} clickId={clickIdMap[`${row.price.storeDbId}|${row.price.url}`]} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </LazySection>

              {/* OPINIONES — the ad lives INSIDE the section so the tab
                  click lands AT the ad, not past it. */}
              <LazySection id="opiniones" refMap={sectionsRef}>
                <AdSlotClient
                  placement="PRODUCT_BETWEEN_PRICES_REVIEWS"
                  category={product.category}
                  brand={product.brand}
                  productId={product.id}
                  className="w-full"
                />
                <div className="bg-background rounded-2xl border p-5 space-y-5 mt-4">
                  <h2 className="text-lg font-bold">Opiniones</h2>
                  <div className="grid md:grid-cols-[180px_1fr] gap-6">
                    <div className="text-center md:text-left">
                      <p className="text-5xl font-black">{ratingAverage ? ratingAverage.toFixed(1) : "—"}</p>
                      <StarsRow value={Math.round(ratingAverage)} size={18} />
                      <p className="text-xs text-muted-foreground mt-1">{ratingCount} {ratingCount === 1 ? "reseña" : "reseñas"}</p>
                    </div>
                    <div className="space-y-1.5">
                      {[5, 4, 3, 2, 1].map((s) => {
                        const count = distribution[s as 1 | 2 | 3 | 4 | 5] ?? 0;
                        const pct = ratingCount ? (count / ratingCount) * 100 : 0;
                        return (
                          <div key={s} className="flex items-center gap-2 text-xs">
                            <span className="w-3">{s}</span>
                            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-yellow-400" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-8 text-right text-muted-foreground">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    {!showReview && (
                      <Button onClick={handleReviewClick}>
                        Escribir reseña
                      </Button>
                    )}
                    {showReview && userIsAuthenticated && (
                      <ReviewForm
                        productId={product.id}
                        stores={product.prices}
                        onCancel={() => setShowReview(false)}
                        onSubmitted={() => { setShowReview(false); }}
                      />
                    )}
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    {(product.reviews ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aún no hay reseñas. ¡Sé el primero!</p>
                    ) : (
                      (product.reviews ?? []).map((r) => (
                        <ReviewCard key={r.id} review={r} />
                      ))
                    )}
                  </div>
                </div>
              </LazySection>

              {/* HISTORIAL */}
              <LazySection id="historial" refMap={sectionsRef}>
                <AdSlotClient
                  placement="PRODUCT_BETWEEN_REVIEWS_HISTORY"
                  category={product.category}
                  brand={product.brand}
                  productId={product.id}
                  className="w-full"
                />
                <div className="bg-background rounded-2xl border p-5 space-y-4 mt-4">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-lg font-bold">Historial de precios</h2>
                    <Button variant="outline" size="sm" onClick={handlePriceAlertClick}>
                      <Bell className="w-4 h-4 mr-1" /> Alerta de precio
                    </Button>
                  </div>
                  <MultiStorePriceChart prices={product.prices} history={product.priceHistory ?? []} />
                </div>
              </LazySection>

              {/* DETALLES */}
              <LazySection id="detalles" refMap={sectionsRef}>
                <AdSlotClient
                  placement="PRODUCT_BETWEEN_HISTORY_DETAILS"
                  category={product.category}
                  brand={product.brand}
                  productId={product.id}
                  className="w-full"
                />
                <div className="bg-background rounded-2xl border p-6 mt-4">
                  <h2 className="text-lg font-bold mb-4">Detalles</h2>
                  {product.detailsContent ? (
                    <TipTapRenderer doc={product.detailsContent} />
                  ) : (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>El equipo SuCom aún no ha agregado detalles para este producto.</p>
                    </div>
                  )}
                  {product.faqContent && product.faqContent.length > 0 && (
                    <div className="mt-6 pt-6 border-t space-y-2">
                      <h3 className="text-base font-semibold mb-2">Preguntas frecuentes</h3>
                      {product.faqContent.map((f, i) => (
                        <details key={i} className="group rounded-lg border px-4 py-2 [&_summary::-webkit-details-marker]:hidden">
                          <summary className="flex items-center justify-between gap-2 cursor-pointer list-none text-sm font-medium py-1">
                            {f.question}
                            <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                          </summary>
                          <p className="text-sm text-muted-foreground pt-1 pb-1">{f.answer}</p>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </LazySection>

              {/* CARACTERISTICAS — ad inside section so the tab lands AT it. */}
              <LazySection id="caracteristicas" refMap={sectionsRef}>
                <AdSlotClient
                  placement="PRODUCT_BETWEEN_DETAILS_SPECS"
                  category={product.category}
                  brand={product.brand}
                  productId={product.id}
                  className="w-full"
                />
                <div className="bg-background rounded-2xl border p-6 mt-4">
                  <h2 className="text-lg font-bold mb-4">
                    {product.specsKind === "NUTRITION" ? "Información nutrimental" : "Características"}
                  </h2>
                  {product.specsContent ? (
                    <TipTapRenderer doc={product.specsContent} />
                  ) : !(product as { nutrition?: any }).nutrition ? (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>
                        El equipo SuCom aún no ha agregado{" "}
                        {product.specsKind === "NUTRITION" ? "información nutrimental" : "características"}{" "}
                        para este producto.
                      </p>
                    </div>
                  ) : null}
                  <div className="mt-4">
                    <NutritionTable nutrition={(product as { nutrition?: any }).nutrition} />
                  </div>
                </div>
              </LazySection>
            </div>
          </div>

          {/* Sidebar 300x600 únicamente al costado de los tabs */}
          <aside className="hidden min-[1200px]:block">
            <div className="sticky top-20">
              <div className="w-[300px] min-h-[600px]">
                <AdSlotClient
                  placement="PRODUCT_SIDEBAR_300x600"
                  category={product.category}
                  brand={product.brand}
                  productId={product.id}
                  className="w-[300px] min-h-[600px]"
                />
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="fixed inset-x-3 bottom-3 z-40 lg:hidden" role="region" aria-label="Alerta de precio rápida">
        <button
          type="button"
          onClick={handlePriceAlertClick}
          className="flex w-full items-center gap-3 rounded-2xl border border-primary/30 bg-background/95 px-4 py-3 text-left shadow-2xl backdrop-blur-sm supports-backdrop-filter:bg-background/90"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Bell className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-black text-foreground">
              {cheapest ? `Avísame si baja de ${formatCurrency(cheapest.price)}` : "Avísame cuando baje"}
            </span>
            <span className="block text-xs text-muted-foreground">Gratis · correo y Passkey segura</span>
          </span>
          <span className="shrink-0 text-sm font-bold text-primary">Activar →</span>
        </button>
      </div>

      {alertOpen && (
        <PriceAlertModal
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          productId={product.id}
          productName={product.name}
          currentPrice={product.lowestPrice}
        />
      )}

      {loginPromptOpen && (
        <LoginPromptModal
          open={loginPromptOpen}
          onClose={() => setLoginPromptOpen(false)}
          title={loginPromptKind === "price-alert" ? "Recibe esta alerta de precio" : "Crea tu cuenta en SuCom"}
          message={loginPromptKind === "price-alert"
            ? "Verifica tu correo y crea una Passkey para avisarte cuando este producto baje. Al volver, retomaremos esta alerta."
            : "Inicia sesión o crea tu cuenta para escribir reseñas y guardar listas."}
          callbackUrl={loginPromptKind === "price-alert" && typeof window !== "undefined"
            ? `${window.location.pathname}?priceAlert=1&utm_source=price_alert&utm_medium=retention&utm_campaign=price_alert`
            : undefined}
        />
      )}

      {shoppingListEnabled && addToListOpen && (
        <AddToListModal
          open={addToListOpen}
          onClose={() => setAddToListOpen(false)}
          productId={product.id}
          productName={product.name}
          soldByWeight={product.soldByWeight}
        />
      )}
    </div>
  );
}

/**
 * Fila de tienda de la sección "Precios en tiendas" (storesprices_v1_1).
 *
 * Escritorio: logo · título en la tienda · beneficio · envío + precio · botón.
 * El envío va inmediatamente a la izquierda del precio, en tono discreto.
 *
 * Móvil: logo/tienda · envío · precio con su botón, todo en la primera línea,
 * y el título de la tienda debajo recortado con "…" si no cabe.
 *
 * Tocar el logo (o la línea de envío) abre `StoreInfoDialog`: modal en
 * escritorio, card que sube desde abajo en móvil.
 */
function StoresProduct({ price, productId, clickId, productName }: { price: ProductPrice; productId?: string; clickId?: number; productName: string }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const discount = price.originalPrice && price.originalPrice > price.price
    ? calculateDiscount(price.originalPrice, price.price)
    : 0;
  const shipping = resolveShipping(price);
  const storeTitle = price.storeProductName ?? "—";

  const openInfo = () => setInfoOpen(true);
  const infoLabel = `Ver información de ${price.storeName}`;

  return (
    <div className="bg-background rounded-2xl border p-4 hover:shadow-xs transition-shadow">
      {/* ---------------------------- ESCRITORIO ---------------------------- */}
      <div className="hidden md:grid grid-cols-[190px_minmax(0,1fr)_120px_auto_150px] items-center gap-4">
        {/* 1. Logo + nombre de la tienda (abre el popup) */}
        <div className="min-w-0">
          <button
            type="button"
            onClick={openInfo}
            aria-label={infoLabel}
            className="flex items-center gap-2 w-full min-w-0 text-left"
          >
            <StoreLogoCircle name={price.storeName} src={price.storeLogo} size={40} />
            <span className="block min-w-0 font-semibold text-sm truncate">{price.storeName}</span>
          </button>
          {!price.inStock && (
            <Badge variant="destructive" className="mt-1 w-fit">Sin stock</Badge>
          )}
        </div>

        {/* 2. Nombre del producto en la tienda */}
        <div className="min-w-0">
          <p className="text-sm text-foreground/90 line-clamp-2">{storeTitle}</p>
        </div>

        {/* 3. Beneficios SuCom */}
        <div className="text-xs">
          <BenefitChip p={price} />
        </div>

        {/* 4. Envío (discreto) a la izquierda del precio */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={openInfo}
            aria-label={infoLabel}
            className={cn(
              "max-w-[140px] text-right text-[11px] leading-tight rounded px-1 py-0.5 hover:underline",
              shipping?.free ? "text-green-700" : "text-muted-foreground"
            )}
          >
            <span className="block">
              <Truck className="w-3 h-3 inline-block mr-1 align-[-0.1em]" aria-hidden="true" />
              {shipping?.cost ?? "Ver envío"}
            </span>
            {shipping?.eta && (
              <span className="block text-muted-foreground">{shipping.eta}</span>
            )}
          </button>
          <div className="text-right shrink-0">
            {discount > 0 && (
              <Badge variant="hot" className="text-[10px] mb-0.5">-{discount}%</Badge>
            )}
            {price.originalPrice && price.originalPrice > price.price && (
              <p className="text-xs text-muted-foreground line-through leading-none">
                {formatCurrency(price.originalPrice)}
              </p>
            )}
            <p className="text-xl font-black text-green-700 leading-tight">{formatCurrency(price.price)}</p>
          </div>
        </div>

        {/* 5. Botón */}
        <Button asChild size="sm" disabled={!price.inStock} className="w-full">
          <GoLink storeId={price.storeId} storeDbId={price.storeDbId} url={price.url} productId={productId} tab="producto" clickId={clickId}>
            Ir a la tienda <ExternalLink className="w-3 h-3 ml-1" />
          </GoLink>
        </Button>
      </div>

      {/* ------------------------------- MÓVIL ------------------------------ */}
      <div className="md:hidden">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={openInfo}
              aria-label={infoLabel}
              className="flex items-center gap-2 w-full min-w-0 text-left"
            >
              <StoreLogoCircle name={price.storeName} src={price.storeLogo} size={36} />
              <span className="block min-w-0 font-semibold text-sm truncate">{price.storeName}</span>
            </button>
            {!price.inStock && (
              <Badge variant="destructive" className="mt-1 w-fit">Sin stock</Badge>
            )}
          </div>

          <button
            type="button"
            onClick={openInfo}
            aria-label={infoLabel}
            className={cn(
              "shrink-0 max-w-[86px] text-[10px] leading-tight text-right",
              shipping?.free ? "text-green-700" : "text-muted-foreground"
            )}
          >
            <Truck className="w-3 h-3 inline-block mr-0.5 align-[-0.1em]" aria-hidden="true" />
            <span className="line-clamp-2">{shipping?.cost ?? "Envío"}</span>
          </button>

          <div className="shrink-0 w-[104px] text-right">
            <div className="flex items-baseline justify-end gap-1">
              {price.originalPrice && price.originalPrice > price.price && (
                <span className="text-[10px] text-muted-foreground line-through">
                  {formatCurrency(price.originalPrice)}
                </span>
              )}
              <span className="text-base font-black text-green-700 leading-none">
                {formatCurrency(price.price)}
              </span>
            </div>
            <Button asChild size="sm" disabled={!price.inStock} className="mt-1 h-8 w-full px-2 text-[11px] whitespace-nowrap">
              <GoLink storeId={price.storeId} storeDbId={price.storeDbId} url={price.url} productId={productId} tab="producto" clickId={clickId}>
                Ir a la tienda
              </GoLink>
            </Button>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2">
          {/* `truncate` recorta el título con "…" cuando se sale del ancho. */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{storeTitle}</p>
          </div>
          {discount > 0 && <Badge variant="hot" className="text-[10px] shrink-0">-{discount}%</Badge>}
          <BenefitChip p={price} />
        </div>
      </div>

      {infoOpen && (
        <StoreInfoDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          price={price}
          productName={productName}
          productId={productId}
          clickId={clickId}
        />
      )}
    </div>
  );
}

function PromotedStoreCard({ price, productId, clickId }: { price: ProductPrice; productId?: string; clickId?: number }) {
  const discount = price.originalPrice && price.originalPrice > price.price
    ? calculateDiscount(price.originalPrice, price.price)
    : 0;
  return (
    <div className="relative bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 shadow-xs">
      <div className="absolute -top-2 left-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full">
        <Megaphone className="w-3 h-3" /> Patrocinado
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-bold text-sm">{price.storeName}</p>
          {price.storeProductName && (
            <p className="text-xs text-muted-foreground line-clamp-2">{price.storeProductName}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ShippingLabel p={price} />
            <BenefitChip p={price} />
          </div>
        </div>
        <div className="text-right shrink-0">
          {discount > 0 && (
            <Badge variant="hot" className="text-[10px] mb-1">-{discount}%</Badge>
          )}
          {price.originalPrice && price.originalPrice > price.price && (
            <p className="text-xs text-muted-foreground line-through">{formatCurrency(price.originalPrice)}</p>
          )}
          <p className="text-xl font-black text-amber-900">{formatCurrency(price.price)}</p>
        </div>
        <Button asChild size="sm" className="shrink-0 bg-amber-500 hover:bg-amber-600">
          <GoLink storeId={price.storeId} storeDbId={price.storeDbId} url={price.url} productId={productId} tab="producto" clickId={clickId}>
            Ir a la tienda <ExternalLink className="w-3 h-3 ml-1" />
          </GoLink>
        </Button>
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: ProductReview }) {
  return (
    <article className="border rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex items-center justify-center text-xs font-bold">
          {review.userAvatar ? (
            <Image src={review.userAvatar} alt={review.userName ?? ""} width={32} height={32} />
          ) : (
            (review.userName ?? "?").charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{review.userName ?? "Usuario"}</p>
          <p className="text-[11px] text-muted-foreground">
            {new Date(review.createdAt).toLocaleDateString("es-MX")}
            {review.storeName ? ` · Comprado en ${review.storeName}` : ""}
          </p>
        </div>
        <StarsRow value={review.rating} />
      </div>
      {review.title && <p className="font-medium text-sm text-foreground">{review.title}</p>}
      <p className="text-sm text-muted-foreground whitespace-pre-line">
        <ReactionRenderer
          text={review.content}
          filter={{ scope: "COMMENT", module: "reviews", contentType: "review" }}
          preserveLineBreaks
        />
      </p>
    </article>
  );
}
